/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { showTTUOption } from '@api/fetchers/ttu';
import { breakpoints } from '@styles/index';
import {
  getTTUFlag,
  getTTUShow,
  getShowTTUCurtain
} from '@stores/selectors/ttuSelectors';
import Dynamic from '@base/Dynamic/Dynamic';
import { getMe } from '@api/selectors/auth';
import { useToggleable } from '@stores/toggleables';
import { LOGIN_DRAWER } from '@constants/toggleables';
import useTTUStore from '@stores/ttu';
import { useIsMobile } from '@stores/AppCommonData';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import useSelectedGoal from '@api/hooks/topology/useSelectedGoal';
import useLoginModalExperiment from '@cont/Login/utils/hooks/useLoginModalExperiment';
import LoginComponent from '@cont/Login/LoginComponent';
import EducatorHeader from '@cont/EducatorProfile/HeaderSection/Header';
import Header from '../Header/Header';
import BaseFooter from '../Footer/BaseFooter';
import LayoutContainer from '../Components/Container';

const TTUWidgetRenderer = Dynamic(() => import('../TTUWidgetRenderer'));
const TTURenderer = Dynamic(() => import('../TTURenderer'));

const Container = styled(LayoutContainer)`
  @media only screen and (max-width: ${breakpoints.tablet}) {
    min-width: 0px;
  }
  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: 100vw;
  }
`;

const AppContainer = styled.div`
  margin-top: 0;
  background-color: var(--color-base-1);
  @media only screen and (max-width: ${breakpoints.tablet}) {
    width: 100%;
  }
`;

const EducatorPageLayout = ({
  children,
  isEducatorSide,
  layoutOptions = {}
}) => {
  const router = useRouter();
  const { query } = router;
  const { authorID } = query;
  useLoginModalExperiment();
  const me = useMyInfo(getMe);
  const { selectedGoal } = useSelectedGoal();
  const { isOpen: showLoginDrawer, closeElement: closeLoginDrawer } =
    useToggleable(LOGIN_DRAWER);
  const isMobile = useIsMobile();
  const ttuFlag = useTTUStore(getTTUFlag);
  const showTTUCurtain = useTTUStore(getShowTTUCurtain);
  const ttuShow = useTTUStore(getTTUShow);
  const [showTTU, setShowTTU] = useState(ttuShow);
  const [showTTUTooltip, setShowTTUTooltip] = useState(false);
  const [ttuTriggerSource, setTTUTriggerSource] = useState('Scroll Trigger');
  const [backButton, setBackButton] = useState({
    value: false,
    fn: () => {}
  });

  const handleBackPress = () => {
    backButton.fn();
  };

  useEffect(() => {
    setShowTTU(ttuShow);
  }, [ttuShow]);
  const {
    hideIntercom = false,
    hideSearch: shouldHideSearch = false,
    showPlusNav = false
  } = layoutOptions;
  useEffect(() => {
    if (ttuFlag && !hideIntercom && !me.isEducator && !isMobile) {
      setTimeout(() => setShowTTUTooltip(true), 15000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.isEducator]);

  const hideSearch = shouldHideSearch;

  const onClose = () => {
    closeLoginDrawer();
  };

  const onCloseTTU = () => {
    setBackButton({ value: false, fn: () => {} });
    setShowTTU(false);
    showTTUCurtain(false);
    showTTUOption(query.goalUID || selectedGoal.uid);
  };

  function renderLogin() {
    return (
      <LoginComponent
        loginProps={{
          setBackButton,
          backButton
        }}
        drawerProps={{
          login: true,
          drawerState: showLoginDrawer,
          onClose,
          showBackButton: backButton.value,
          hideCloseButton: backButton.value,
          onPressBack: handleBackPress
        }}
      />
    );
  }

  function renderEducatorHeader() {
    return <EducatorHeader username={authorID.replace('@', '')} />;
  }

  const insideApp = query.inside_app === 'true' || false;
  const insideIframeLogin = query.is_iframe_login === 'true' || false;

  return (
    <Container>
      {!insideApp && !insideIframeLogin && <Header hideSearch={hideSearch} />}
      <AppContainer>
        {renderEducatorHeader()}
        {children}
      </AppContainer>
      {!insideApp && !insideIframeLogin && (
        <BaseFooter display={!showPlusNav && !isEducatorSide} />
      )}
      {renderLogin()}
      {!isMobile && (
        <TTURenderer
          onCloseTTU={onCloseTTU}
          showTTU={showTTU}
          backButton={backButton}
          setBackButton={setBackButton}
          handleBackPress={handleBackPress}
          ttuTriggerSource={ttuTriggerSource}
        />
      )}
      {!isMobile && (
        <TTUWidgetRenderer
          showTTU={showTTU}
          setShowTTU={setShowTTU}
          showTTUTooltip={showTTUTooltip}
          setShowTTUTooltip={setShowTTUTooltip}
          ttuTriggerSource={ttuTriggerSource}
          setTTUTriggerSource={setTTUTriggerSource}
          hideIntercom={hideIntercom}
        />
      )}
    </Container>
  );
};

export default EducatorPageLayout;
